import React from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { useApi } from "@/lib/api";
import { PageHeader, Panel, StatusBadge, PageLoader, Btn, SectionTitle } from "@/lib/ui";
import { Download, Table2 } from "lucide-react";

const FORMATS = ["xlsx", "csv", "pdf", "json"];

export default function Downloads() {
  const api = useApi();
  const { data, isLoading } = useQuery({ queryKey: ["downloads"], queryFn: () => api.get("/downloads/catalog") });

  const exportData = async (dataset, fmt) => {
    try {
      await api.download(`/downloads/export?dataset=${dataset}&fmt=${fmt}`, `${dataset}.${fmt}`);
      toast.success(`Exported ${dataset}.${fmt}`);
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (isLoading) return <PageLoader />;

  return (
    <div>
      <PageHeader kicker="Content" title="Download Centre" />
      <p className="text-sm text-muted-foreground mb-5 max-w-2xl">
        Export authorised datasets in XLSX, CSV, PDF or JSON. Datasets awaiting brand-data connection
        are marked configuration-required and will activate as brands are integrated in later phases.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {data?.catalog.map((d) => (
          <Panel key={d.key} className="p-4" data-testid={`export-${d.key}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Table2 className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium capitalize">{d.key.replace(/_/g, " ")}</span>
              </div>
              {d.available
                ? <StatusBadge tone="ok">{d.count} rows</StatusBadge>
                : <StatusBadge tone="warn">Config Required</StatusBadge>}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {FORMATS.map((f) => (
                <Btn key={f} size="sm" variant="outline" disabled={!d.available}
                  onClick={() => exportData(d.key, f)} data-testid={`export-${d.key}-${f}`}>
                  <Download className="h-3 w-3" /> {f.toUpperCase()}
                </Btn>
              ))}
            </div>
          </Panel>
        ))}
      </div>
    </div>
  );
}
